import ReplaceHostButton from './ReplaceHostButton'
import ConfirmDeleteButton from '../confirmation/ConfirmDeleteButton'
import React from 'react'
import {Alert, Box, Button, Paper, Stack, Typography} from '@mui/material'
import {useSessionControls} from './SessionContext'
import {projectTimer} from './domain/timerSnapshot'
import SynchronizationIndicator from './SynchronizationIndicator'

const formatTime = (milliseconds: number | null) => {
  if (milliseconds === null) return 'Sudden Death'
  const seconds = Math.ceil(milliseconds / 1000)
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}
const SessionDate = ({milliseconds}: {milliseconds: number}) => <time dateTime={new Date(milliseconds).toISOString()}>{new Date(milliseconds).toLocaleString(undefined, {dateStyle: 'medium', timeStyle: 'short'})}</time>

const Output = ({name, children}: {name: string; children: React.ReactNode}) => <Box>
  <Typography variant="overline" color="text.secondary">{name}</Typography>
  <Typography component="output" role="status" aria-label={name} sx={{display: 'block', overflowWrap: 'anywhere'}}>{children}</Typography>
</Box>

export default function SessionGrid() {
  const controls = useSessionControls()
  const {session, status, syncStatus, busy, error, serverNowMs} = controls
  const timer = session ? projectTimer(session.timer, serverNowMs) : null
  const segment = session?.timer.clock.kind === 'suddenDeath' ? 'Sudden Death' : session?.timer.onBreak ? `Break after level ${session.timer.levelIndex + 1}` : `Level ${(session?.timer.levelIndex ?? 0) + 1}`
  const showResume = controls.canResume
  return <Paper sx={{p: {xs: 2.5, sm: 3.5}}}>
    <Stack spacing={3}>
      <Typography variant="h5" component="h2">{session ? 'Current session' : 'Start a session'}</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Output name="Hosting status">{status}</Output>
      <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}><Output name="Synchronization status">{syncStatus}</Output><SynchronizationIndicator/></Stack>
      {status === 'Recovering' && <Alert severity="info">Recovery is waiting for a connection.</Alert>}
      {status === 'Legacy' && <><Alert severity="warning">Legacy sessions cannot be restored. Retire them before explicitly creating another session.</Alert>
        <Button disabled={busy} onClick={() => void controls.retire()}>Retire legacy sessions</Button></>}
      {session && <>
        <Output name="Session ID">{session.sessionId}</Output>
        <Output name="Session schedule">{session.schedule.name}</Output>
        <Output name="Session segment">{segment}</Output>
        <Output name="Session time remaining">{formatTime(timer?.remainingMs ?? null)}</Output>
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={3}>
          <Output name="Session created"><SessionDate milliseconds={session.createdAtMs}/></Output>
        </Stack>
        <Output name="Session playing">{timer?.playing ? 'Running' : 'Paused'}</Output>
      </>}
      {status === 'Hosting' && <Box sx={{alignSelf: 'flex-start'}}><ConfirmDeleteButton key={session?.sessionId} label="Delete Session" subject="session" description={`The session for ${session?.schedule.name} will be deleted. Your local tournament will continue.`} disabled={busy} onConfirm={() => {void controls.remove()}}/></Box>}
      {status === 'Standalone' && <Button variant="contained" disabled={busy} onClick={() => void controls.create()} sx={{alignSelf: 'flex-start'}}>Create Session</Button>}
      {status === 'View only' && session && <ReplaceHostButton key={session.sessionId}/>}
      {showResume && <Button disabled={busy} variant="contained" onClick={() => void controls.resume()}>Resume hosting</Button>}
      {(status === 'Recovering' || status === 'View only' || syncStatus !== 'Synced') && <Button disabled={busy} onClick={() => void controls.retry()}>Retry connection</Button>}
    </Stack>
  </Paper>
}
